import { Injectable } from '@angular/core';
import { User } from '../interfaces/user.interface';
import { Tenant } from '../interfaces/tenant.interface';

/**
 * Pagai brand id in the multi-brand Intercom workspace. Forces every
 * Intercom boot (authenticated and anonymous) to route to the Pagai
 * messenger styling and team, even when the workspace serves several
 * products.
 */
const PAGAI_INTERCOM_BRAND_ID = '7457';
const INTERCOM_API_BASE = 'https://api-iam.intercom.io';

/**
 * Extend Window interface to include Intercom global function
 */
declare global {
  interface Window {
    /**
     * Intercom global function for messaging API
     * See: https://developers.intercom.com/installing-intercom/web/methods
     */
    Intercom: any;
    /**
     * Intercom global config object, read by the widget script as soon
     * as it loads. Setting `brand_id` here, BEFORE the script tag, is
     * the officially recommended way to bind the messenger to a
     * specific brand in multi-brand workspaces.
     */
    intercomSettings?: Record<string, unknown>;
  }
}

/**
 * Intercom service for managing the Intercom messenger widget.
 * Handles dynamic script loading, initialization, and lifecycle management.
 *
 * ## Usage
 * The service is automatically managed by AppComponent via effect:
 * - Initializes when user and tenant are available
 * - Shuts down when user logs out
 *
 * @example
 * ```typescript
 * // Automatic initialization (handled by AppComponent)
 * void this._intercomService.initialize(user, tenant, appId);
 *
 * // Manual shutdown
 * this._intercomService.shutdown();
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  /**
   * Tracks whether Intercom is currently initialized
   * @private
   */
  private _isInitialized = false;

  /**
   * Tracks whether the Intercom script has been loaded from CDN
   * @private
   */
  private _isScriptLoaded = false;

  /**
   * Promise for ongoing initialization to prevent duplicate calls
   * @private
   */
  private _initializationPromise: Promise<void> | null = null;

  /**
   * Initialize Intercom with user and tenant data.
   * This method is idempotent - calling it multiple times is safe.
   *
   * @param user - Current authenticated user
   * @param tenant - Current tenant
   * @param appId - Intercom application ID
   * @returns Promise that resolves when initialization is complete
   */
  public async initialize(user: User, tenant: Tenant, appId: string): Promise<void> {
    // Early return if no app ID configured
    if (!appId) {
      return;
    }

    // If already initialized, skip
    if (this._isInitialized) {
      return;
    }

    // Return existing initialization if in progress
    if (this._initializationPromise) {
      return this._initializationPromise;
    }

    this._initializationPromise = this._performInitialization(user, tenant, appId);
    return this._initializationPromise;
  }

  /**
   * Perform the actual Intercom initialization
   * @param user - Current authenticated user
   * @param tenant - Current tenant
   * @param appId - Intercom application ID
   * @returns Promise that resolves when initialization is complete
   * @private
   */
  private async _performInitialization(user: User, tenant: Tenant, appId: string): Promise<void> {
    try {
      // Load Intercom script if not already loaded
      if (!this._isScriptLoaded) {
        await this._loadIntercomScript(appId);
        this._isScriptLoaded = true;
      }

      // Parse user creation timestamp
      const createdAtTimestamp = user.createdAt
        ? Math.floor(new Date(user.createdAt).getTime() / 1000)
        : Math.floor(Date.now() / 1000);

      // Boot Intercom with user data
      window.Intercom('boot', {
        api_base: INTERCOM_API_BASE,
        app_id: appId,
        brand_id: PAGAI_INTERCOM_BRAND_ID,
        user_id: user.sub,
        email: user.email,
        name: `${user.name} ${user.surname}`.trim(),
        phone: user.phone,
        created_at: createdAtTimestamp,
        language_override: user.lang || 'it', // Force Italian language (or use user's language preference)
        hide_default_launcher: true, // Hide default launcher, trigger from sidebar button
        alignment: 'left', // Open messenger on the left side (near sidebar button)
        company: {
          id: tenant.id,
          name: tenant.name,
        },
      });

      this._isInitialized = true;
      this._initializationPromise = null;
    } catch (error) {
      console.error('[IntercomService] Failed to initialize:', error);
      this._initializationPromise = null;
      throw error;
    }
  }

  /**
   * Load Intercom script dynamically from CDN.
   * @param appId - Intercom application ID
   * @returns Promise that resolves when script is loaded
   * @private
   */
  private _loadIntercomScript(appId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      if (document.querySelector(`script[src*="widget.intercom.io"]`)) {
        resolve();
        return;
      }

      // Seed `window.intercomSettings` BEFORE the widget script loads:
      // brand_id is the first thing the widget reads to bind the
      // messenger to the right brand in a multi-brand workspace.
      window.intercomSettings = {
        ...(window.intercomSettings ?? {}),
        api_base: INTERCOM_API_BASE,
        app_id: appId,
        brand_id: PAGAI_INTERCOM_BRAND_ID,
      };

      // Initialize stub function
      window.Intercom =
        window.Intercom ||
        function (...args: any[]) {
          (window.Intercom.q = window.Intercom.q || []).push(args);
        };
      window.Intercom.q = window.Intercom.q || [];

      // Create and inject script
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://widget.intercom.io/widget/${appId}`;

      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Intercom script'));

      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode?.insertBefore(script, firstScript);
    });
  }

  /**
   * Shutdown Intercom and clear user session.
   * This should be called on logout or when user becomes unauthenticated.
   */
  public shutdown(): void {
    if (!this._isInitialized) {
      return;
    }

    try {
      if (window.Intercom) {
        window.Intercom('shutdown');
      }

      this._isInitialized = false;
    } catch (error) {
      console.error('[IntercomService] Failed to shutdown:', error);
      this._isInitialized = false;
    }
  }

  /**
   * Show the Intercom messenger.
   */
  public show(): void {
    if (!this._isInitialized) {
      return;
    }
    window.Intercom('show');
  }

  /**
   * Boot Intercom anonymously (no user/tenant identification) and open the
   * messenger. Used pre-auth (e.g. on the account-deactivated page) where
   * no logged-in user data is available.
   */
  public showAnonymous(appId: string): void {
    if (typeof window === 'undefined' || !window.Intercom) return;
    try {
      window.Intercom('boot', {
        api_base: INTERCOM_API_BASE,
        app_id: appId,
        brand_id: PAGAI_INTERCOM_BRAND_ID,
      });
      window.Intercom('show');
    } catch (error) {
      console.error('[IntercomService] Failed to show anonymous:', error);
    }
  }

  /**
   * Hide the Intercom messenger.
   */
  public hide(): void {
    if (!this._isInitialized) {
      return;
    }
    window.Intercom('hide');
  }

  /**
   * Update user data in Intercom.
   * @param userData - Updated user data
   */
  public updateUser(userData: any): void {
    if (window.Intercom) {
      window.Intercom('update', userData);
    }
  }

  /**
   * Track custom events in Intercom.
   * @param eventName - Name of the event
   * @param metadata - Optional event metadata
   */
  public trackEvent(eventName: string, metadata?: any): void {
    if (window.Intercom) {
      window.Intercom('trackEvent', eventName, metadata);
    }
  }

  /**
   * Show new message composer with optional pre-filled text.
   * @param prefilledMessage - Optional message to pre-fill
   */
  public showNewMessage(prefilledMessage?: string): void {
    if (window.Intercom) {
      if (prefilledMessage) {
        window.Intercom('showNewMessage', prefilledMessage);
      } else {
        window.Intercom('showNewMessage');
      }
    }
  }
}
